import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Badge } from '../../../components/ui/Badge';
import { Skeleton } from '../../../components/ui/Skeleton';
import { 
  useInvestmentProducts, 
  useCreateInvestmentProduct, 
  useUpdateInvestmentProduct,
  useToggleProductAvailability 
} from '../../../hooks/useInvestments';
import type { InvestmentProductType, RiskRating, RateType, LiquidityType, CreateProductInput } from '../../../types/investments';

const typeLabels: Record<InvestmentProductType, string> = {
  CDB: 'CDB',
  LCI: 'LCI',
  LCA: 'LCA',
  FUND: 'Fundo',
  DEBENTURE: 'Debênture',
  CRI: 'CRI',
  CRA: 'CRA',
};

const riskColors: Record<RiskRating, 'success' | 'primary' | 'default' | 'error'> = {
  VERY_LOW: 'success',
  LOW: 'success',
  MEDIUM: 'primary',
  HIGH: 'error',
  VERY_HIGH: 'error',
};

const riskLabels: Record<RiskRating, string> = {
  VERY_LOW: 'Muito Baixo',
  LOW: 'Baixo',
  MEDIUM: 'Médio',
  HIGH: 'Alto',
  VERY_HIGH: 'Muito Alto',
};

const rateTypeLabels: Record<RateType, string> = {
  FIXED: 'Taxa Fixa',
  CDI_PERCENTAGE: '% do CDI',
  CDI_PLUS: 'CDI +',
  IPCA_PLUS: 'IPCA +',
};

export function InvestmentProductsAdmin() {
  const [showForm, setShowForm] = useState(false);
  const [typeFilter, setTypeFilter] = useState<InvestmentProductType | undefined>();
  const { data: productsData, isLoading } = useInvestmentProducts({ type: typeFilter });
  const createProduct = useCreateInvestmentProduct();
  const toggleAvailability = useToggleProductAvailability();

  const [formData, setFormData] = useState<CreateProductInput>({
    name: '',
    description: '',
    type: 'CDB',
    issuer: '',
    custodianId: '',
    rateType: 'CDI_PERCENTAGE',
    rate: 100,
    minimumAmount: 100,
    liquidityType: 'DAILY',
    liquidityDays: 0,
    riskRating: 'LOW',
    taxExempt: false,
  });

  const handleCreate = async () => {
    await createProduct.mutateAsync({
      ...formData,
      minimumAmount: formData.minimumAmount * 100,
    });
    setShowForm(false);
    setFormData({
      name: '',
      description: '',
      type: 'CDB',
      issuer: '',
      custodianId: '',
      rateType: 'CDI_PERCENTAGE',
      rate: 100,
      minimumAmount: 100,
      liquidityType: 'DAILY',
      liquidityDays: 0,
      riskRating: 'LOW',
      taxExempt: false,
    });
  };

  const handleToggle = async (id: string, available: boolean) => {
    await toggleAvailability.mutateAsync({ id, available: !available });
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatRate = (product: { rateType: RateType; rate: number; indexer?: string }) => {
    switch (product.rateType) {
      case 'FIXED':
        return `${product.rate.toFixed(2)}% a.a.`;
      case 'CDI_PERCENTAGE':
        return `${product.rate.toFixed(0)}% do CDI`;
      case 'CDI_PLUS':
        return `CDI + ${product.rate.toFixed(2)}%`;
      case 'IPCA_PLUS':
        return `IPCA + ${product.rate.toFixed(2)}%`;
      default:
        return `${product.rate}%`;
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Produtos de Investimento</h1>
          <p className="text-gray-500">Gerencie o catálogo de produtos</p>
        </div>
        <Button onClick={() => setShowForm(true)}>Novo Produto</Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant={!typeFilter ? 'primary' : 'secondary'}
              onClick={() => setTypeFilter(undefined)}
            >
              Todos
            </Button>
            {Object.entries(typeLabels).map(([type, label]) => (
              <Button
                key={type}
                size="sm"
                variant={typeFilter === type ? 'primary' : 'secondary'}
                onClick={() => setTypeFilter(type as InvestmentProductType)}
              >
                {label}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Create Form */}
      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle>Novo Produto</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="block text-sm font-medium mb-1">Nome</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ex: CDB Liquidez Diária"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Tipo</label>
                <select
                  className="w-full border rounded-md px-3 py-2"
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value as InvestmentProductType })}
                >
                  {Object.entries(typeLabels).map(([type, label]) => (
                    <option key={type} value={type}>{label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Emissor</label>
                <Input
                  value={formData.issuer}
                  onChange={(e) => setFormData({ ...formData, issuer: e.target.value })}
                  placeholder="Ex: Banco XYZ"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Custodiante ID</label>
                <Input
                  value={formData.custodianId}
                  onChange={(e) => setFormData({ ...formData, custodianId: e.target.value })}
                  placeholder="ID do custodiante"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Tipo de Taxa</label>
                <select
                  className="w-full border rounded-md px-3 py-2"
                  value={formData.rateType}
                  onChange={(e) => setFormData({ ...formData, rateType: e.target.value as RateType })}
                >
                  {Object.entries(rateTypeLabels).map(([type, label]) => (
                    <option key={type} value={type}>{label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Taxa</label>
                <Input
                  type="number"
                  value={formData.rate}
                  onChange={(e) => setFormData({ ...formData, rate: parseFloat(e.target.value) })}
                  step={0.01}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Valor Mínimo (R$)</label>
                <Input
                  type="number"
                  value={formData.minimumAmount}
                  onChange={(e) => setFormData({ ...formData, minimumAmount: parseFloat(e.target.value) })}
                  min={0}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Liquidez</label>
                <select
                  className="w-full border rounded-md px-3 py-2"
                  value={formData.liquidityType}
                  onChange={(e) => setFormData({ ...formData, liquidityType: e.target.value as LiquidityType })}
                >
                  <option value="DAILY">Diária</option>
                  <option value="AT_MATURITY">No Vencimento</option>
                  <option value="GRACE_PERIOD">Após Carência</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Risco</label>
                <select
                  className="w-full border rounded-md px-3 py-2"
                  value={formData.riskRating}
                  onChange={(e) => setFormData({ ...formData, riskRating: e.target.value as RiskRating })}
                >
                  {Object.entries(riskLabels).map(([risk, label]) => (
                    <option key={risk} value={risk}>{label}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.taxExempt}
                    onChange={(e) => setFormData({ ...formData, taxExempt: e.target.checked })}
                    className="rounded"
                  />
                  <span>Isento de IR (LCI/LCA)</span>
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-4 mt-6">
              <Button variant="secondary" onClick={() => setShowForm(false)}>
                Cancelar
              </Button>
              <Button onClick={handleCreate} disabled={createProduct.isPending}>
                {createProduct.isPending ? 'Criando...' : 'Criar Produto'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Products List */}
      <Card>
        <CardHeader>
          <CardTitle>Produtos ({productsData?.total || 0})</CardTitle>
        </CardHeader>
        <CardContent>
          {productsData?.products && productsData.products.length > 0 ? (
            <div className="space-y-4">
              {productsData.products.map((product) => (
                <div
                  key={product.id}
                  className="flex items-center justify-between p-4 border rounded-lg"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium">{product.name}</h3>
                      <Badge variant="default">{typeLabels[product.type]}</Badge>
                      <Badge variant={riskColors[product.riskRating]}>
                        {riskLabels[product.riskRating]}
                      </Badge>
                      {product.taxExempt && (
                        <Badge variant="success">Isento IR</Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      {product.issuer} • {formatRate(product)} • Mín: {formatCurrency(product.minimumAmount)}
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <Badge variant={product.isAvailable ? 'success' : 'default'}>
                      {product.isAvailable ? 'Disponível' : 'Indisponível'}
                    </Badge>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => handleToggle(product.id, product.isAvailable)}
                      disabled={toggleAvailability.isPending}
                    >
                      {product.isAvailable ? 'Desativar' : 'Ativar'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-gray-500 py-8">
              Nenhum produto encontrado
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default InvestmentProductsAdmin;
